import {
  ASSISTANT_PROVIDERS,
  assistantConnectionSummary,
  CONNECTABLE_ASSISTANTS,
} from '@/components/integrations/assistants/assistant-providers';
import { NAV_DARK_FOCUS_RING } from '@/components/scaffold/layout/nav-dark';
import { useMcpConnectionsQuery } from '@/hooks/queries/use-mcp-query';
import { usePlanGates } from '@/hooks/use-plan-gates';
import { PlanLimit } from '@fluenceur/types';
import { Box, Flex, Image, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

const LOGO_BUBBLE = {
  boxSize: '30px',
  rounded: 'full',
  bg: 'white',
  borderWidth: '2px',
  borderColor: 'lilac.300',
  display: 'grid',
  placeItems: 'center',
  flex: 'none',
} as const;

/**
 * Lilac card above the menu footer that takes a brand to the AI assistants of the
 * Integrations page, where Claude and ChatGPT are connected. The two logos overlap
 * the way the design draws them; the sentence carries the meaning, so they stay
 * decorative.
 *
 * It leaves the menu once both assistants are connected. While the connections are
 * still loading it stays hidden, so a brand that is already set up never sees it
 * flash; if they cannot be read, it shows, since connecting is still possible.
 */
export const AssistantsConnectCard: React.FC = () => {
  const { t } = useTranslation();
  const { data, isError } = useMcpConnectionsQuery();
  const { mcpLocked, openUpsell } = usePlanGates();

  if (!data && !isError) return null;
  const allConnected = CONNECTABLE_ASSISTANTS.every(
    (provider) => assistantConnectionSummary(data?.connections, provider).isConnected
  );
  if (allConnected) return null;

  const content = (
    <>
      <Flex align="center" flex="none" aria-hidden="true">
        <Box {...LOGO_BUBBLE}>
          <Image src={ASSISTANT_PROVIDERS.claude.logo} alt="" boxSize="17px" />
        </Box>
        <Box {...LOGO_BUBBLE} ml="-9px">
          <Image src={ASSISTANT_PROVIDERS.chatgpt.logo} alt="" boxSize="17px" />
        </Box>
      </Flex>
      <Text fontSize="13.5px" fontWeight="semibold" letterSpacing="-0.01em" lineHeight="1.2">
        {t('navigation.assistantsCard')}
      </Text>
    </>
  );

  return (
    <Flex
      asChild
      align="center"
      gap="11px"
      px="12px"
      py="11px"
      rounded="14px"
      bg="lilac.300"
      color="darkNav"
      textAlign="left"
      cursor="pointer"
      transition="background-color .15s"
      _hover={{ bg: 'lilac.200', textDecoration: 'none' }}
      _focusVisible={NAV_DARK_FOCUS_RING}
    >
      {/* Without connected assistants in its plan, the card presents them
          (upsell) rather than leading to a connection the API refuses. */}
      {mcpLocked ? (
        <button type="button" onClick={() => openUpsell(PlanLimit.MCP)}>
          {content}
        </button>
      ) : (
        <RouterLink to="/settings/integrations">{content}</RouterLink>
      )}
    </Flex>
  );
};
